import { Fragment, useMemo, useState } from 'react';
import type { Dipendente, Evento, GiornoCalendario } from '../api/tipi.ts';
import { opzioniFiltri } from '../calendario/controlli.ts';
import {
  NOMI_MESI,
  type RigaAnalisi,
  analizza,
  distribuzioneMensile,
  giorniPiuScoperti,
  mesiDi,
  righeDeiTotali,
  totaliPerReparto,
} from './calcoli.ts';
import { esportaCsv, esportaExcel, esportaPdf } from './esporta.ts';
import stili from './AnalisiDati.module.css';

/**
 * Analisi dati.
 *
 * Tutto e' espresso anche in ORE. Nel file attuale il riepilogo mette "giorni
 * ferie" accanto a "ore permessi" e i due numeri non si sommano: non si riesce
 * a dire quanto sia stata assente una persona senza fare i conti a mano. Qui
 * una giornata vale 8 ore e il totale diventa confrontabile fra persone,
 * reparti e mesi.
 */

export interface ProprietaAnalisi {
  anno: number;
  dipendenti: readonly Dipendente[];
  eventi: readonly Evento[];
  giorni: readonly GiornoCalendario[];
}

type Vista = 'persone' | 'mesi' | 'reparti' | 'scoperture';

const VISTE: { codice: Vista; etichetta: string }[] = [
  { codice: 'persone', etichetta: 'Per persona' },
  { codice: 'mesi', etichetta: 'Andamento mensile' },
  { codice: 'reparti', etichetta: 'Per reparto' },
  { codice: 'scoperture', etichetta: 'Giorni più scoperti' },
];

export function AnalisiDati({ anno, dipendenti, eventi, giorni }: ProprietaAnalisi) {
  const [vista, setVista] = useState<Vista>('persone');
  const [sede, setSede] = useState('');
  const [reparto, setReparto] = useState('');
  const [mese, setMese] = useState('');
  const [soloTotali, setSoloTotali] = useState(true);

  const { sedi, reparti } = opzioniFiltri(dipendenti, sede || null);

  const selezionati = useMemo(
    () =>
      dipendenti.filter(
        (d) =>
          (sede === '' || d.sedeCodice === sede) &&
          (reparto === '' || d.repartoCodice === reparto),
      ),
    [dipendenti, sede, reparto],
  );

  const idSelezionati = useMemo(() => new Set(selezionati.map((d) => d.id)), [selezionati]);
  const eventiSelezionati = useMemo(
    () => eventi.filter((e) => idSelezionati.has(e.dipendenteId)),
    [eventi, idSelezionati],
  );

  const tutteLeRighe = useMemo(
    () => analizza(selezionati, eventiSelezionati, giorni, anno),
    [selezionati, eventiSelezionati, giorni, anno],
  );

  const righe = useMemo(() => {
    let r = tutteLeRighe;
    if (mese !== '') r = r.filter((x) => x.periodo === `${anno}-${mese}`);
    else if (soloTotali) r = r.filter((x) => x.periodo === String(anno));
    return r.sort(
      (a, b) =>
        a.sede.localeCompare(b.sede) ||
        a.reparto.localeCompare(b.reparto) ||
        a.dipendente.localeCompare(b.dipendente) ||
        a.periodo.localeCompare(b.periodo),
    );
  }, [tutteLeRighe, mese, soloTotali, anno]);

  // Il periodo scelto in cima: l'anno, oppure un mese. Vale per tutte le
  // viste, non solo per la tabella delle persone.
  const periodo = mese === '' ? String(anno) : `${anno}-${mese}`;

  const mensile = useMemo(() => distribuzioneMensile(tutteLeRighe, anno), [tutteLeRighe, anno]);
  const perReparto = useMemo(
    () => totaliPerReparto(tutteLeRighe, periodo),
    [tutteLeRighe, periodo],
  );
  const scoperture = useMemo(
    () => giorniPiuScoperti(selezionati, eventiSelezionati, giorni, mese === '' ? String(anno) : periodo),
    [selezionati, eventiSelezionati, giorni, periodo, mese, anno],
  );

  const descrizione = [
    sede === '' ? 'tutte le sedi' : sedi.find((s) => s.codice === sede)?.nome,
    reparto === '' ? null : reparti.find((r) => r.codice === reparto)?.nome,
    mese === '' ? `anno ${anno}` : `${NOMI_MESI[Number(mese) - 1]} ${anno}`,
  ]
    .filter(Boolean)
    .join(' · ');

  // Il periodo non e' piu' una colonna della tabella, quindi deve stare nel
  // nome del file: aprendo fra sei mesi un "analisi-2026.csv" non si saprebbe
  // se e' l'anno intero o un mese solo. Il PDF lo dice gia' nel sottotitolo.
  const nomeFile = mese === '' ? `analisi-${anno}` : `analisi-${anno}-${mese}`;

  // I riquadri guardano le righe del periodo, non quelle mostrate in tabella:
  // la spunta "Solo totali annuali" governa il dettaglio dell'elenco, non i
  // totali, e mescolare le due cose li raddoppiava.
  const totali = righeDeiTotali(tutteLeRighe, anno, mese).reduce(
    (acc, r) => ({
      giorniFerie: acc.giorniFerie + r.giorniFerie,
      oreFerie: acc.oreFerie + r.oreFerie,
      oreRecupero: acc.oreRecupero + r.oreRecupero,
      ore104: acc.ore104 + r.ore104,
      oreMf: acc.oreMf + r.oreMf,
      oreDs: acc.oreDs + r.oreDs,
      oreMalattia: acc.oreMalattia + r.oreMalattia,
      oreCp: acc.oreCp + r.oreCp,
      giorniRiposo: acc.giorniRiposo + r.giorniRiposo,
      oreTotali: acc.oreTotali + r.oreTotali,
    }),
    { giorniFerie: 0, oreFerie: 0, oreRecupero: 0, ore104: 0, oreMf: 0, oreDs: 0, oreMalattia: 0, oreCp: 0, giorniRiposo: 0, oreTotali: 0 },
  );

  return (
    <div className={stili.pagina}>
      <header className={stili.testata}>
        <h1 className={stili.titolo}>Analisi dati</h1>
        <div className={stili.esportazioni}>
          <button
            type="button"
            className={stili.pulsante}
            onClick={() => esportaExcel(righe, nomeFile)}
          >
            Excel
          </button>
          <button
            type="button"
            className={stili.pulsante}
            onClick={() => esportaCsv(righe, nomeFile)}
          >
            CSV
          </button>
          <button
            type="button"
            className={stili.pulsante}
            onClick={() => esportaPdf(righe, `Analisi assenze ${anno}`, descrizione)}
          >
            PDF
          </button>
        </div>
      </header>

      <div className={stili.filtri}>
        <select className={stili.select} value={sede} onChange={(e) => { setSede(e.target.value); setReparto(''); }} aria-label="Sede">
          <option value="">Tutte le sedi</option>
          {sedi.map((s) => <option key={s.codice} value={s.codice}>{s.nome}</option>)}
        </select>

        <select className={stili.select} value={reparto} onChange={(e) => setReparto(e.target.value)} aria-label="Reparto">
          <option value="">Tutti i reparti</option>
          {reparti.map((r) => <option key={r.codice} value={r.codice}>{r.nome}</option>)}
        </select>

        <select className={stili.select} value={mese} onChange={(e) => setMese(e.target.value)} aria-label="Mese">
          <option value="">Tutto l'anno</option>
          {NOMI_MESI.map((m, i) => (
            <option key={m} value={String(i + 1).padStart(2, '0')}>{m}</option>
          ))}
        </select>

        {mese === '' && (
          <label className={stili.spunta}>
            <input type="checkbox" checked={soloTotali} onChange={(e) => setSoloTotali(e.target.checked)} />
            Solo totali annuali
          </label>
        )}
      </div>

      <div className={stili.riepilogo}>
        <Riquadro etichetta="Persone" valore={new Set(righe.map((r) => r.dipendenteId)).size} />
        <Riquadro etichetta="Giorni ferie" valore={arrotonda(totali.giorniFerie)} />
        <Riquadro etichetta="Ore ferie" valore={arrotonda(totali.oreFerie)} />
        <Riquadro etichetta="Ore recupero" valore={arrotonda(totali.oreRecupero)} />
        <Riquadro etichetta="Ore legge 104" valore={arrotonda(totali.ore104)} />
        <Riquadro etichetta="Ore MF" valore={arrotonda(totali.oreMf)} />
        <Riquadro etichetta="Ore donazione sangue" valore={arrotonda(totali.oreDs)} />
        <Riquadro etichetta="Ore malattia" valore={arrotonda(totali.oreMalattia)} />
        <Riquadro etichetta="Ore congedo paternità" valore={arrotonda(totali.oreCp)} />
        <Riquadro etichetta="Ore totali (escl. 104, MF, CP, DS, malattia)" valore={arrotonda(totali.oreTotali)} evidenza />
      </div>

      <div className={stili.viste}>
        {VISTE.map((v) => (
          <button
            key={v.codice}
            type="button"
            className={`${stili.vista} ${vista === v.codice ? stili.vistaAttiva : ''}`}
            onClick={() => setVista(v.codice)}
            aria-pressed={vista === v.codice}
          >
            {v.etichetta}
          </button>
        ))}
      </div>

      <div className={stili.contenuto}>
        {vista === 'persone' && (
          <TabellaPersone righe={righe} tutteLeRighe={tutteLeRighe} anno={anno} mese={mese} />
        )}
        {vista === 'mesi' && (
          <>
            <Andamento mensile={mensile} />
            {/* Il grafico disegna le stesse ore dei riquadri: senza dirlo, chi
                confronta una barra con le ore delle paghe trova numeri diversi
                e non sa perche'. */}
            <p className={stili.didascalia}>
              Ore di assenza per mese, <strong>escluse 104, MF, CP, donazione sangue e
              malattia</strong>.
            </p>
          </>
        )}
        {vista === 'reparti' && (
          <>
            <p className={stili.spiegazione}>
              {mese === ''
                ? `Ore per reparto nell’anno ${anno}. Scegliendo un mese in alto la tabella mostra quel mese.`
                : `Ore per reparto in ${NOMI_MESI[Number(mese) - 1]} ${anno}.`}
            </p>
            <TabellaReparti dati={perReparto} />
          </>
        )}
        {vista === 'scoperture' && <TabellaScoperture dati={scoperture} />}
      </div>
    </div>
  );
}

function Riquadro({ etichetta, valore, evidenza }: { etichetta: string; valore: number; evidenza?: boolean }) {
  return (
    <div className={`${stili.riquadro} ${evidenza ? stili.riquadroEvidenza : ''}`}>
      <span className={stili.riquadroValore}>{String(valore).replace('.', ',')}</span>
      <span className={stili.riquadroEtichetta}>{etichetta}</span>
    </div>
  );
}

function TabellaPersone({
  righe,
  tutteLeRighe,
  anno,
  mese,
}: {
  righe: readonly RigaAnalisi[];
  tutteLeRighe: readonly RigaAnalisi[];
  anno: number;
  /** Il mese scelto in alto, per evidenziarlo nel dettaglio. */
  mese: string;
}) {
  // Quale persona e' aperta. Il dettaglio mese per mese sta sotto la sua riga
  // invece che in un'altra pagina: la domanda "e questo totale, com'e' fatto?"
  // nasce guardando la riga, e va risposta li'.
  const [aperta, setAperta] = useState<string | null>(null);

  if (righe.length === 0) return <p className={stili.vuoto}>Nessun dato per i filtri scelti.</p>;
  return (
    <div className={stili.scorrevole}>
      <table className={stili.tabella}>
        <thead>
          <tr>
            <th>Sede / Reparto</th><th>Dipendente</th>
            <th className={stili.num}>GG ferie</th>
            <th className={stili.num}>ORE ferie</th>
            <th className={stili.num}>ORE recupero</th>
            <th className={stili.num}>ORE 104</th>
            <th className={stili.num}>ORE MF</th>
            <th className={stili.num}>ORE DS</th>
            <th className={stili.num}>ORE malattia</th>
            <th className={stili.num}>ORE CP</th>
            <th className={stili.num}>GG riposo</th>
            <th className={stili.num}>ORE totali (escl. 104, MF, CP, DS, malattia)</th>
          </tr>
        </thead>
        <tbody>
          {righe.map((r) => {
            const apertaQui = aperta === r.dipendenteId;
            const mesi = apertaQui ? mesiDi(tutteLeRighe, r.dipendenteId, anno) : [];
            return (
              <Fragment key={`${r.dipendenteId}-${r.periodo}`}>
                <tr
                  className={`${stili.rigaApribile} ${apertaQui ? stili.rigaAperta : ''}`}
                  onClick={() => setAperta(apertaQui ? null : r.dipendenteId)}
                  aria-expanded={apertaQui}
                >
                  <td className={stili.tenue}>{r.sede} · {r.reparto}</td>
                  <td>
                    <span className={stili.freccia} aria-hidden="true">{apertaQui ? '▾' : '▸'}</span>
                    {r.dipendente}
                  </td>
                  <Numeri riga={r} />
                </tr>
                {apertaQui && mesi.length === 0 && (
                  <tr className={stili.rigaMese}>
                    <td />
                    <td colSpan={10} className={stili.tenue}>
                      Nessuna assenza registrata nell’anno.
                    </td>
                  </tr>
                )}
                {mesi.map((m) => (
                  // Con un mese scelto in alto, la riga della persona mostra
                  // quel mese: evidenziarlo nel dettaglio dice da dove viene
                  // quel numero, invece di lasciar cercare la corrispondenza.
                  <tr
                    key={`${m.dipendenteId}-${m.periodo}`}
                    className={`${stili.rigaMese} ${
                      mese !== '' && m.periodo === `${anno}-${mese}` ? stili.meseScelto : ''
                    }`}
                  >
                    <td />
                    <td className={stili.tenue}>{NOMI_MESI[Number(m.periodo.slice(5, 7)) - 1]}</td>
                    <Numeri riga={m} />
                  </tr>
                ))}
              </Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

/** Le colonne numeriche, identiche per la riga della persona e per i suoi mesi. */
function Numeri({ riga }: { riga: RigaAnalisi }) {
  return (
    <>
      <td className={stili.num}>{numero(riga.giorniFerie)}</td>
      <td className={stili.num}>{numero(riga.oreFerie)}</td>
      <td className={stili.num}>{numero(riga.oreRecupero)}</td>
      <td className={stili.num}>{numero(riga.ore104)}</td>
      <td className={stili.num}>{numero(riga.oreMf)}</td>
      <td className={stili.num}>{numero(riga.oreDs)}</td>
      <td className={stili.num}>{numero(riga.oreMalattia)}</td>
      <td className={stili.num}>{numero(riga.oreCp)}</td>
      <td className={stili.num}>{numero(riga.giorniRiposo)}</td>
      <td className={`${stili.num} ${stili.forte}`}>{numero(riga.oreTotali)}</td>
    </>
  );
}

/**
 * Istogramma dell'andamento mensile.
 *
 * Barre in CSS invece di una libreria di grafici: e' una sola serie di dodici
 * valori, e aggiungere un pacchetto di grafica al bundle per questo sarebbe
 * sproporzionato. Se serviranno grafici veri si valutera' allora.
 */
function Andamento({ mensile }: { mensile: readonly number[] }) {
  const massimo = Math.max(...mensile, 1);
  return (
    <div className={stili.istogramma}>
      {mensile.map((ore, i) => (
        <div key={NOMI_MESI[i]} className={stili.colonna}>
          <span className={stili.valoreBarra}>{ore > 0 ? numero(ore) : ''}</span>
          <div
            className={stili.barra}
            style={{ height: `${Math.max(2, (ore / massimo) * 100)}%` }}
            title={`${NOMI_MESI[i]}: ${numero(ore)} ore, escluse 104, MF, CP, DS e malattia`}
          />
          <span className={stili.etichettaBarra}>{NOMI_MESI[i]}</span>
        </div>
      ))}
    </div>
  );
}

function TabellaReparti({ dati }: { dati: readonly { reparto: string; persone: number; oreTotali: number; oreMedie: number }[] }) {
  if (dati.length === 0) return <p className={stili.vuoto}>Nessun dato.</p>;
  return (
    <div className={stili.scorrevole}>
      <table className={stili.tabella}>
        <thead>
          <tr>
            <th>Sede / Reparto</th>
            <th className={stili.num}>Persone</th>
            <th className={stili.num}>Ore totali (escl. 104, MF, CP, DS, malattia)</th>
            <th className={stili.num}>Ore medie a persona</th>
          </tr>
        </thead>
        <tbody>
          {dati.map((r) => (
            <tr key={r.reparto}>
              <td>{r.reparto}</td>
              <td className={stili.num}>{r.persone}</td>
              <td className={stili.num}>{numero(r.oreTotali)}</td>
              <td className={`${stili.num} ${stili.forte}`}>{numero(r.oreMedie)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function TabellaScoperture({ dati }: { dati: readonly { data: string; reparto: string; assenti: number; organico: number }[] }) {
  if (dati.length === 0) return <p className={stili.vuoto}>Nessuna assenza registrata.</p>;
  return (
    <>
      <p className={stili.spiegazione}>
        I giorni con più persone assenti nello stesso reparto. È l'informazione che
        oggi richiede di scorrere la griglia a mano, e serve a scoprire una scopertura
        prima che arrivi. Segue i filtri in alto: sede, reparto e mese.
      </p>
      <div className={stili.scorrevole}>
        <table className={stili.tabella}>
          <thead>
            <tr>
              <th>Giorno</th><th>Sede / Reparto</th>
              <th className={stili.num}>Assenti</th>
              <th className={stili.num}>Su organico</th>
              <th className={stili.num}>Quota</th>
            </tr>
          </thead>
          <tbody>
            {dati.map((r) => {
              const quota = r.organico > 0 ? Math.round((r.assenti / r.organico) * 100) : 0;
              return (
                <tr key={`${r.data}-${r.reparto}`}>
                  <td>{r.data.split('-').reverse().join('/')}</td>
                  <td>{r.reparto}</td>
                  <td className={stili.num}>{r.assenti}</td>
                  <td className={stili.num}>{r.organico}</td>
                  <td className={`${stili.num} ${quota >= 50 ? stili.allarme : ''}`}>{quota}%</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </>
  );
}

function numero(n: number): string {
  return String(Math.round(n * 100) / 100).replace('.', ',');
}

function arrotonda(n: number): number {
  return Math.round(n * 100) / 100;
}
